import { z } from 'zod';
import { isoDateSchema, uuidSchema } from './common.js';
/**
 * Connections: the software a business already runs on, bringing its
 * customers, jobs and invoices into the books.
 *
 * Every source, a CRM's API or a spreadsheet exported from one, lands as the
 * same four things (customer, job, invoice, and what is still owed), so a new
 * CRM is an adapter and never a new feature.
 */
export const CONNECTOR_PROVIDERS = [
    'housecall_pro',
    'jobber',
    'quickbooks_online',
    'servicetitan',
];
export const connectorProviderSchema = z.enum(CONNECTOR_PROVIDERS);
const counts = z.object({
    created: z.number().int(),
    updated: z.number().int(),
    skipped: z.number().int(),
});
export const invoiceCountsSchema = z.object({
    created: z.number().int(),
    updated: z.number().int(),
    unchanged: z.number().int(),
    linked: z.number().int(),
    voided: z.number().int(),
    flagged: z.number().int(),
    skipped: z.number().int(),
});
/** What one sync did, in counts a person can read back. */
export const connectorSyncSummarySchema = z.object({
    customers: counts,
    jobs: counts,
    invoices: invoiceCountsSchema,
});
/** A connection as a person may see it: never the key or the token. */
export const connectorConnectionSchema = z.object({
    provider: connectorProviderSchema,
    status: z.enum(['connected', 'failing', 'disconnected']),
    /** The last four of an API key; null for a sign-in. */
    secretHint: z.string().nullable(),
    accountLabel: z.string().nullable(),
    connectedAt: z.string(),
    lastSyncedAt: z.string().nullable(),
    lastError: z.string().nullable(),
    lastSummary: connectorSyncSummarySchema.nullable(),
});
export const CONNECTOR_AVAILABILITY = ['available', 'awaiting_approval', 'coming_soon'];
/** One entry in the catalog of systems a business can connect. */
export const connectorCatalogEntrySchema = z.object({
    provider: connectorProviderSchema,
    name: z.string(),
    tagline: z.string(),
    auth: z.enum(['api_key', 'oauth']),
    availability: z.enum(CONNECTOR_AVAILABILITY),
    /** Where a person finds the key, for an API-key connection. */
    keyHelp: z.string().nullable(),
    connection: connectorConnectionSchema.nullable(),
});
export const connectorCatalogSchema = z.object({
    connectors: z.array(connectorCatalogEntrySchema),
});
export const connectApiKeyRequestSchema = z.object({
    apiKey: z.string().trim().min(10).max(500),
});
export const oauthStartSchema = z.object({ url: z.url() });
// ---------------------------------------------------------------------------
// Spreadsheet imports
// ---------------------------------------------------------------------------
/** The fields an invoice import fills, in the order a person reads them. */
export const IMPORT_FIELDS = [
    'invoiceNumber',
    'issuedOn',
    'customer',
    'subtotal',
    'tax',
    'total',
    'outstanding',
    'status',
    'jobNumber',
];
export const importFieldSchema = z.enum(IMPORT_FIELDS);
/** Field → the column header that fills it. A field left out is not in the file. */
export const importColumnMapSchema = z.partialRecord(importFieldSchema, z.string().max(200));
export const importOptionsSchema = z.object({
    /** How the file writes dates, when its numbers alone cannot say. */
    dateOrder: z.enum(['mdy', 'dmy']).default('mdy'),
});
const fileSchema = z.object({
    fileName: z.string().min(1).max(255),
    /** The file itself, base64. CSV or Excel, up to about 3 MB. */
    contentBase64: z.string().min(1).max(4_200_000),
});
export const importPreviewRequestSchema = fileSchema;
/** How a column was matched, so the screen can say so. */
export const matchSourceSchema = z.enum(['saved', 'name', 'shape', 'ai']);
export const importPreviewSchema = z.object({
    headers: z.array(z.string()),
    /** The first rows, as text, for the person to see what each column holds. */
    sample: z.array(z.array(z.string())),
    rowCount: z.number().int(),
    sheetName: z.string().nullable(),
    /** A layout seen before: its name, and the mapping that was used. */
    profile: z
        .object({ id: uuidSchema, name: z.string(), lastUsedAt: z.string().nullable() })
        .nullable(),
    suggestion: z.object({
        columnMap: importColumnMapSchema,
        sources: z.partialRecord(importFieldSchema, matchSourceSchema),
        options: importOptionsSchema,
        /** True when the dates could be read either way and the guess may be wrong. */
        dateOrderUncertain: z.boolean(),
    }),
    /** Whether asking the AI to look at the columns is possible right now. */
    aiAvailable: z.boolean(),
});
export const importSuggestRequestSchema = z.object({
    headers: z.array(z.string().max(200)).min(1).max(100),
    sample: z.array(z.array(z.string().max(200))).max(8),
});
export const importSuggestionSchema = z.object({
    columnMap: importColumnMapSchema,
});
/** One row as it will land: what the screen previews before anything is saved. */
export const importedRowSchema = z.object({
    row: z.number().int(),
    invoiceNumber: z.string().nullable(),
    issuedOn: isoDateSchema.nullable(),
    customer: z.string().nullable(),
    subtotalMinor: z.number().int().nullable(),
    taxMinor: z.number().int().nullable(),
    totalMinor: z.number().int().nullable(),
    outstandingMinor: z.number().int().nullable(),
    voided: z.boolean(),
    jobNumber: z.string().nullable(),
    /** Why this row will not be used, or a warning about it. */
    problem: z.string().nullable(),
});
export const importRunRequestSchema = fileSchema.extend({
    name: z.string().trim().min(1).max(80),
    columnMap: importColumnMapSchema,
    options: importOptionsSchema,
    /** True: read and check every row, save nothing. */
    dryRun: z.boolean().default(false),
});
export const importProblemSchema = z.object({
    row: z.number().int().nullable(),
    problem: z.string(),
});
export const importRunResultSchema = invoiceCountsSchema.extend({
    rows: z.number().int(),
    runId: uuidSchema.nullable(),
    problems: z.array(importProblemSchema),
    /** On a dry run, every row as it would land. */
    preview: z.array(importedRowSchema),
    totals: z.object({
        invoicedMinor: z.number().int(),
        taxMinor: z.number().int(),
        from: isoDateSchema.nullable(),
        through: isoDateSchema.nullable(),
    }),
});
export const importHistorySchema = z.object({
    runs: z.array(z.object({
        id: uuidSchema,
        fileName: z.string().nullable(),
        profileName: z.string().nullable(),
        rowCount: z.number().int(),
        createdCount: z.number().int(),
        updatedCount: z.number().int(),
        linkedCount: z.number().int(),
        voidedCount: z.number().int(),
        flaggedCount: z.number().int(),
        skippedCount: z.number().int(),
        createdAt: z.string(),
    })),
});
//# sourceMappingURL=connections.js.map